// src/services/analytics.ts
// Uses Firestore for analytics tracking (Firebase Analytics disabled)
import { db } from './firebase';
import { NICHES, resolveNicheKey } from '../constants/niches';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  doc,
  getDoc,
  setDoc,
  increment,
  limit as firestoreLimit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';

// Types
export interface PageView {
  id?: string;
  path: string;
  title: string;
  timestamp: Timestamp;
  userAgent: string;
  referrer: string;
  sessionId: string;
  visitorIp?: string;
  language?: string;
  screenResolution?: string;
  platform?: string;
  country?: string;
  countryCode?: string;
  region?: string;
  city?: string;
  isp?: string;
  browser?: string;
  device?: string;
  /** which specialist site/niche the visit landed on (ai, mobile, web, ..., full) */
  niche?: string;
  /** hostname of the site visited (e.g. mordecai-ai.web.app) */
  host?: string;
}

export interface AnalyticsStats {
  totalViews: number;
  uniqueVisitors: number;
  todayViews: number;
  weekViews: number;
  monthViews: number;
  topPages: { path: string; views: number }[];
  topCountries: { country: string; countryCode: string; views: number }[];
  topReferrers: { referrer: string; views: number }[];
  topSites: { niche: string; label: string; views: number; visitors: number }[];
  viewsByDay: { date: string; views: number }[];
  recentViews: PageView[];
}

// Generate a session ID for tracking unique visitors
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('portfolio_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('portfolio_session_id', sessionId);
  }
  return sessionId;
};

interface VisitorInfo {
  ip: string;
  country?: string;
  countryCode?: string;
  region?: string;
  city?: string;
  isp?: string;
}

// Fetch visitor info from backend (cached per session)
const getVisitorInfo = async (): Promise<VisitorInfo> => {
  try {
    const cached = sessionStorage.getItem('visitor_info');
    if (cached) return JSON.parse(cached);

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const url = apiUrl.startsWith('http') ? apiUrl : `https://${apiUrl}`;
    const resp = await fetch(`${url.replace(/\/$/, '')}/api/visitor-info`, {
      signal: AbortSignal.timeout(5000),
    });
    const data = await resp.json();
    if (data.ip) {
      sessionStorage.setItem('visitor_info', JSON.stringify(data));
      return data;
    }
  } catch {
    // Silently fail - visitor tracking is best-effort
  }
  return { ip: 'unknown' };
};

// Parse browser name from user agent
const parseBrowser = (ua: string): string => {
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Chrome/') && !ua.includes('Edg/')) return 'Chrome';
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('MSIE') || ua.includes('Trident/')) return 'IE';
  return 'Other';
};

// Parse device type from user agent
const parseDevice = (ua: string): string => {
  if (/iPad|Tablet/i.test(ua)) return 'Tablet';
  if (/iPhone|Android.*Mobile|Mobile/i.test(ua)) return 'Mobile';
  return 'Desktop';
};

// Track a page view
export const trackPageView = async (path: string, title: string): Promise<void> => {
  try {
    // Don't track admin pages
    if (path.startsWith('/admin')) {
      return;
    }

    const sessionId = getSessionId();
    const visitorInfo = await getVisitorInfo();
    const ua = navigator.userAgent;
    const host = window.location.hostname;
    const niche = resolveNicheKey(host, window.location.search);
    const pageView: Omit<PageView, 'id'> = {
      path,
      title,
      timestamp: Timestamp.now(),
      userAgent: ua,
      referrer: document.referrer || 'direct',
      sessionId,
      visitorIp: visitorInfo.ip,
      language: navigator.language || 'unknown',
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      platform: navigator.platform || 'unknown',
      country: visitorInfo.country || '',
      countryCode: visitorInfo.countryCode || '',
      region: visitorInfo.region || '',
      city: visitorInfo.city || '',
      isp: visitorInfo.isp || '',
      browser: parseBrowser(ua),
      device: parseDevice(ua),
      niche,
      host,
    };

    // Add to Firestore
    await addDoc(collection(db, 'pageViews'), pageView);

    // Update daily stats
    const today = new Date().toISOString().split('T')[0];
    const dailyStatsRef = doc(db, 'analyticsDaily', today);
    await setDoc(dailyStatsRef, {
      date: today,
      views: increment(1),
      updatedAt: Timestamp.now()
    }, { merge: true });

    // Update page-specific stats
    const pageStatsRef = doc(db, 'analyticsPages', encodeURIComponent(path));
    await setDoc(pageStatsRef, {
      path,
      views: increment(1),
      lastVisited: Timestamp.now()
    }, { merge: true });

    // Firebase Analytics disabled - using Firestore-based tracking only
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track custom events
export const trackEvent = async (
  eventName: string,
  eventParams?: Record<string, string | number>
): Promise<void> => {
  try {
    // Firebase Analytics disabled - using Firestore-based tracking only

    // Also store in Firestore for admin visibility
    await addDoc(collection(db, 'analyticsEvents'), {
      eventName,
      params: eventParams || {},
      timestamp: Timestamp.now(),
      sessionId: getSessionId(),
    });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Get analytics stats for admin dashboard
export const getAnalyticsStats = async (): Promise<AnalyticsStats> => {
  try {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(todayStart);
    weekStart.setDate(weekStart.getDate() - 7);
    const monthStart = new Date(todayStart);
    monthStart.setDate(monthStart.getDate() - 30);

    // Get page views from last 30 days
    const viewsQuery = query(
      collection(db, 'pageViews'),
      where('timestamp', '>=', Timestamp.fromDate(monthStart)),
      orderBy('timestamp', 'desc')
    );
    const viewsSnapshot = await getDocs(viewsQuery);
    const views = viewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as PageView[];

    // Calculate stats
    const todayViews = views.filter(v =>
      v.timestamp.toDate() >= todayStart
    ).length;

    const weekViews = views.filter(v =>
      v.timestamp.toDate() >= weekStart
    ).length;

    const monthViews = views.length;

    // Unique visitors (by session ID)
    const uniqueSessions = new Set(views.map(v => v.sessionId));
    const uniqueVisitors = uniqueSessions.size;

    // Top pages
    const pageCounts: Record<string, number> = {};
    views.forEach(v => {
      pageCounts[v.path] = (pageCounts[v.path] || 0) + 1;
    });
    const topPages = Object.entries(pageCounts)
      .map(([path, viewCount]) => ({ path, views: viewCount }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    // Views by day (last 30 days)
    const viewsByDayMap: Record<string, number> = {};
    for (let i = 0; i < 30; i++) {
      const date = new Date(todayStart);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      viewsByDayMap[dateStr] = 0;
    }
    views.forEach(v => {
      const dateStr = v.timestamp.toDate().toISOString().split('T')[0];
      if (viewsByDayMap[dateStr] !== undefined) {
        viewsByDayMap[dateStr]++;
      }
    });
    const viewsByDay = Object.entries(viewsByDayMap)
      .map(([date, viewCount]) => ({ date, views: viewCount }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Top countries
    const countryCounts: Record<string, { country: string; countryCode: string; views: number }> = {};
    views.forEach(v => {
      const code = v.countryCode || v.country || '';
      if (code) {
        if (!countryCounts[code]) {
          countryCounts[code] = { country: v.country || code, countryCode: v.countryCode || '', views: 0 };
        }
        countryCounts[code].views++;
      }
    });
    const topCountries = Object.values(countryCounts)
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    // Top referrers
    const referrerCounts: Record<string, number> = {};
    views.forEach(v => {
      const ref = v.referrer === 'direct' ? 'Direct' : (() => {
        try { return new URL(v.referrer).hostname.replace('www.', ''); } catch { return v.referrer; }
      })();
      referrerCounts[ref] = (referrerCounts[ref] || 0) + 1;
    });
    const topReferrers = Object.entries(referrerCounts)
      .map(([referrer, viewCount]) => ({ referrer, views: viewCount }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    // Traffic by specialist site / niche
    const siteAgg: Record<string, { views: number; sessions: Set<string> }> = {};
    views.forEach(v => {
      const key = v.niche || 'full';
      if (!siteAgg[key]) siteAgg[key] = { views: 0, sessions: new Set() };
      siteAgg[key].views++;
      siteAgg[key].sessions.add(v.sessionId);
    });
    const topSites = Object.entries(siteAgg)
      .map(([niche, agg]) => ({
        niche,
        label: NICHES[niche as keyof typeof NICHES]?.label || (niche === 'full' ? 'Main (Generalist)' : niche),
        views: agg.views,
        visitors: agg.sessions.size,
      }))
      .sort((a, b) => b.views - a.views);

    // Get total views from all time
    const totalStatsRef = doc(db, 'analyticsTotal', 'stats');
    const totalStatsDoc = await getDoc(totalStatsRef);
    let totalViews = monthViews;
    if (totalStatsDoc.exists()) {
      totalViews = totalStatsDoc.data().totalViews || monthViews;
    }

    // Recent views (last 20)
    const recentViews = views.slice(0, 20);

    return {
      totalViews,
      uniqueVisitors,
      todayViews,
      weekViews,
      monthViews,
      topPages,
      topCountries,
      topReferrers,
      topSites,
      viewsByDay,
      recentViews,
    };
  } catch (error) {
    console.error('Error getting analytics stats:', error);
    return {
      totalViews: 0,
      uniqueVisitors: 0,
      todayViews: 0,
      weekViews: 0,
      monthViews: 0,
      topPages: [],
      topCountries: [],
      topReferrers: [],
      topSites: [],
      viewsByDay: [],
      recentViews: [],
    };
  }
};

// Get paginated visitor history with date range filtering
export const getVisitorHistory = async (options: {
  startDate: Date;
  endDate: Date;
  pageSize: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
}): Promise<{
  visitors: PageView[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}> => {
  try {
    const { startDate, endDate, pageSize, lastDoc } = options;

    const q = lastDoc
      ? query(
          collection(db, 'pageViews'),
          where('timestamp', '>=', Timestamp.fromDate(startDate)),
          where('timestamp', '<=', Timestamp.fromDate(endDate)),
          orderBy('timestamp', 'desc'),
          startAfter(lastDoc),
          firestoreLimit(pageSize + 1),
        )
      : query(
          collection(db, 'pageViews'),
          where('timestamp', '>=', Timestamp.fromDate(startDate)),
          where('timestamp', '<=', Timestamp.fromDate(endDate)),
          orderBy('timestamp', 'desc'),
          firestoreLimit(pageSize + 1),
        );
    const snapshot = await getDocs(q);

    const hasMore = snapshot.docs.length > pageSize;
    const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;
    const visitors = docs.map(d => ({ id: d.id, ...d.data() })) as PageView[];
    const newLastDoc = docs.length > 0 ? docs[docs.length - 1] : null;

    return { visitors, lastDoc: newLastDoc, hasMore };
  } catch (error) {
    console.error('Error getting visitor history:', error);
    return { visitors: [], lastDoc: null, hasMore: false };
  }
};

// Track contact form submission
export const trackContactSubmission = async (formData: {
  name: string;
  email: string;
  subject?: string;
}): Promise<void> => {
  await trackEvent('contact_form_submit', {
    subject: formData.subject || 'No subject',
  });
};

// Track project view
export const trackProjectView = async (projectId: string, projectTitle: string): Promise<void> => {
  await trackEvent('project_view', {
    project_id: projectId,
    project_title: projectTitle,
  });
};

// Track service view
export const trackServiceView = async (serviceId: string, serviceTitle: string): Promise<void> => {
  await trackEvent('service_view', {
    service_id: serviceId,
    service_title: serviceTitle,
  });
};

// Track resume download
export const trackResumeDownload = async (): Promise<void> => {
  await trackEvent('resume_download');
};

// Track social link click
export const trackSocialClick = async (platform: string): Promise<void> => {
  await trackEvent('social_click', { platform });
};
